import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/base/SEO';

export default function PaymentPage() {
  const { t } = useTranslation(['payment', 'common']);
  const [selectedService, setSelectedService] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const services: {
    id: string;
    title: string;
    duration: string;
    newDuration?: string;
    price: string;
    newPrice?: string;
    features: string[];
    popular: boolean;
  }[] = [
    {
      id: 'vip-1month',
      title: t('payment:service1.title'),
      duration: t('payment:service1.duration'),
      price: t('payment:service1.price'),
      features: [
        t('payment:service1.feature1'),
        t('payment:service1.feature2'),
        t('payment:service1.feature3'),
        t('payment:service1.feature4'),
        t('payment:service1.feature5'),
      ],
      popular: true,
    },
    {
      id: 'vip-2month',
      title: t('payment:service2.title'),
      duration: t('payment:service2.duration'),
      price: t('payment:service2.price'),
      features: [
        t('payment:service2.feature1'),
        t('payment:service2.feature2'),
        t('payment:service2.feature3'),
        t('payment:service2.feature4'),
        t('payment:service2.feature5'),
      ],
      popular: false,
    },
    {
      id: 'fmd-protocol',
      title: t('payment:service3.title'),
      duration: t('payment:service3.duration'),
      price: t('payment:service3.price'),
      features: [
        t('payment:service3.feature1'),
        t('payment:service3.feature2'),
        t('payment:service3.feature3'),
        t('payment:service3.feature4'),
        t('payment:service3.feature5'),
      ],
      popular: false,
    },
    {
      id: 'child-nutrition',
      title: t('payment:service4.title'),
      duration: t('payment:service4.duration'),
      price: t('payment:service4.price'),
      features: [
        t('payment:service4.feature1'),
        t('payment:service4.feature2'),
        t('payment:service4.feature3'),
        t('payment:service4.feature4'),
        t('payment:service4.feature5'),
      ],
      popular: false,
    },
    {
      id: 'group-intensive',
      title: t('payment:service5.title'),
      duration: t('payment:service5.duration'),
      price: t('payment:service5.price'),
      features: [
        t('payment:service5.feature1'),
        t('payment:service5.feature2'),
        t('payment:service5.feature3'),
        t('payment:service5.feature4'),
        t('payment:service5.feature5'),
      ],
      popular: false,
    },
    {
      id: 'analysis-review',
      title: t('payment:service6.title'),
      duration: t('payment:service6.duration'),
      price: t('payment:service6.price'),
      features: [
        t('payment:service6.feature1'),
        t('payment:service6.feature2'),
        t('payment:service6.feature3'),
        t('payment:service6.feature4'),
      ],
      popular: false,
    },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new FormData(form);

      const response = await fetch('https://readdy.ai/api/form/d6cqfritehdqnvnpihpg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formDataToSend as any).toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '', service: '', message: '' });
        setSelectedService('');
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <SEO
        title={t('payment:seo.title')}
        description={t('payment:seo.description')}
        keywords={t('payment:seo.keywords')}
        ogImage="https://public.readdy.ai/ai/img_res/f7a37abca7009ade7aceb201322ef676.jpg"
      />
      <Navigation />

      <div className="pt-24 sm:pt-32 pb-14 sm:pb-20 px-5 sm:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8 sm:mb-16">
            <h1 className="text-2xl sm:text-4xl font-light text-gray-800 mb-3 sm:mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('payment:hero.title')}
            </h1>
            <p className="text-sm sm:text-lg font-light text-gray-600 max-w-2xl mx-auto leading-relaxed">
              {t('payment:hero.description')}
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8 mb-12 sm:mb-20">
            {services.map((service) => (
              <article
                key={service.id}
                className={`relative bg-white rounded-lg p-5 sm:p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                  selectedService === service.id ? 'ring-2 ring-[#8B9D83] shadow-xl' : 'shadow-md'
                } ${service.popular ? 'border-2 border-[#8B9D83]' : ''}`}
                onClick={() => setSelectedService(service.id)}
              >
                {service.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#8B9D83] text-white px-4 py-1 rounded-full text-xs font-light whitespace-nowrap">
                    {t('payment:popularChoice')}
                  </div>
                )}
                <div className="mb-4 sm:mb-6">
                  <h2 className="text-lg sm:text-2xl font-light text-gray-800 mb-1 sm:mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                    {service.title}
                  </h2>
                  <div className="flex items-center gap-2">
                    {service.newDuration ? (
                      <>
                        <span className="text-xs text-gray-400 font-light line-through">{service.duration}</span>
                        <span className="text-xs font-semibold text-gray-800">{service.newDuration}</span>
                      </>
                    ) : (
                      <span className="text-xs sm:text-sm text-gray-500 font-light">{service.duration}</span>
                    )}
                  </div>
                </div>
                <div className="mb-4 sm:mb-6">
                  {service.newPrice ? (
                    <div className="flex items-center gap-3">
                      <span className="text-xl font-light text-gray-400 line-through">{service.price}</span>
                      <span className="text-2xl font-semibold text-gray-800">{service.newPrice}</span>
                    </div>
                  ) : (
                    <span className="text-2xl sm:text-4xl font-light text-[#8B9D83]">{service.price}</span>
                  )}
                </div>
                <ul className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
                  {service.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-xs sm:text-sm font-light text-gray-600">
                      <i className="ri-check-line text-[#8B9D83] text-base flex-shrink-0 w-4 h-4 flex items-center justify-center mt-0.5" aria-hidden="true"></i>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setSelectedService(service.id)}
                  className={`w-full py-2.5 sm:py-3 rounded-lg text-sm font-light transition-all whitespace-nowrap cursor-pointer ${
                    selectedService === service.id
                      ? 'bg-[#8B9D83] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                  aria-label={t('payment:selectServiceAria', { service: service.title })}
                >
                  {selectedService === service.id ? t('payment:selected') : t('payment:select')}
                </button>
              </article>
            ))}
          </div>

          {/* Booking Form */}
          <div className="max-w-3xl mx-auto bg-white rounded-lg shadow-lg p-6 sm:p-10">
            <h2 className="text-xl sm:text-3xl font-light text-gray-800 mb-2 sm:mb-3 text-center" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('payment:form.title')}
            </h2>
            <p className="text-center text-sm sm:text-base text-gray-600 font-light mb-5 sm:mb-8">
              {t('payment:form.description')}
            </p>

            <form id="booking-form" onSubmit={handleSubmit} data-readdy-form>
              <div className="space-y-4 sm:space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-light text-gray-700 mb-1.5 sm:mb-2">
                    {t('payment:form.name.label')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B9D83] text-sm font-light"
                    placeholder={t('payment:form.name.placeholder')}
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-light text-gray-700 mb-1.5 sm:mb-2">
                    {t('payment:form.email.label')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B9D83] text-sm font-light"
                    placeholder={t('payment:form.email.placeholder')}
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-light text-gray-700 mb-1.5 sm:mb-2">
                    {t('payment:form.phone.label')} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B9D83] text-sm font-light"
                    placeholder={t('payment:form.phone.placeholder')}
                  />
                </div>

                <div>
                  <label htmlFor="service" className="block text-sm font-light text-gray-700 mb-1.5 sm:mb-2">
                    {t('payment:form.service.label')} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={selectedService || formData.service}
                    onChange={(e) => {
                      handleInputChange(e);
                      setSelectedService(e.target.value);
                    }}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B9D83] text-sm font-light cursor-pointer"
                  >
                    <option value="">{t('payment:form.service.placeholder')}</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.id}>
                        {service.title} — {service.newDuration ?? service.duration} ({service.newPrice ?? service.price})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-light text-gray-700 mb-1.5 sm:mb-2">
                    {t('payment:form.message.label')}
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8B9D83] text-sm font-light resize-none"
                    placeholder={t('payment:form.message.placeholder')}
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1 font-light">
                    {formData.message.length}/500 {t('payment:form.message.characters')}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#8B9D83] text-white py-3.5 sm:py-4 rounded-lg font-light text-base sm:text-lg hover:bg-[#7A8C73] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? t('payment:form.submitting') : t('payment:form.submit')}
                </button>

                {submitStatus === 'success' && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm font-light" role="alert">
                    {t('payment:form.successMessage')}
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm font-light" role="alert">
                    {t('payment:form.errorMessage')}
                  </div>
                )}
              </div>
            </form>
          </div>

          {/* Free Consultation CTA */}
          <div className="mt-10 sm:mt-20 text-center bg-gradient-to-r from-[#8B9D83]/10 to-[#B8D4A8]/10 rounded-lg p-6 sm:p-12">
            <h3 className="text-xl sm:text-3xl font-light text-gray-800 mb-3 sm:mb-4 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {t('payment:cta.title')}
            </h3>
            <p className="text-sm sm:text-lg font-light text-gray-600 mb-5 sm:mb-6 max-w-2xl mx-auto">
              {t('payment:cta.description')}
            </p>
            <a
              href="#booking-form"
              className="inline-block bg-white text-[#8B9D83] px-6 sm:px-8 py-3 sm:py-4 rounded-lg font-light text-sm sm:text-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('booking-form')?.scrollIntoView({ behavior: 'smooth' });
              }}
              aria-label={t('payment:cta.buttonAria')}
            >
              {t('payment:cta.button')}
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}